import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  FOCUS_COMMAND,
  $getRoot,
} from "lexical";

// Plugins
import ImagePasteDropPlugin from "./plugins/ImagePasteDropPlugin";
import HoverNodeActionsPlugin from "./plugins/HoverNodeActionsPlugin";
import TablePastePlugin from "./plugins/TablePastePlugin";
import ToolbarPlugin from "./ToolbarPlugin";

// Nodes
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableNode, TableRowNode, TableCellNode } from "@lexical/table";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ImageNode } from "./nodes/ImageNode";
import { StyledTextNode } from "./nodes/StyledTextNode";

const NODES = [
  ListNode,
  ListItemNode,
  LinkNode,
  HeadingNode,
  QuoteNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  HorizontalRuleNode,
  ImageNode,
  StyledTextNode,
];

function normalizeCssColor(value) {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isIgnorableTableBackground(rawColor) {
  const color = normalizeCssColor(rawColor);

  if (
    color === "white" ||
    color === "#fff" ||
    color === "#ffffff" ||
    color === "rgb(255, 255, 255)" ||
    color === "rgb(255 255 255)" ||
    color === "rgb(255,255,255)" ||
    color === "rgba(255, 255, 255, 1)" ||
    color === "rgba(255,255,255,1)" ||
    color === "rgba(255 255 255 / 1)" ||
    color === "rgb(242, 243, 245)" ||
    color === "rgb(242 243 245)" ||
    color === "rgb(242,243,245)"
  ) {
    return true;
  }

  // Catch other "basically white / default grey" values
  const nums = color.match(/\d+/g);
  if (!nums || nums.length < 3) return false;

  const [r, g, b] = nums.slice(0, 3).map(Number);
  return r >= 235 && g >= 235 && b >= 235;
}

function sanitizeTableCellBackgrounds(html) {
  if (!html || typeof html !== "string") return "";

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;

  wrapper.querySelectorAll("td, th").forEach((cell) => {
    const style = cell.getAttribute("style");
    if (!style) return;

    const declarations = style
      .split(";")
      .map((part) => part.trim())
      .filter(Boolean);

    const kept = [];

    for (const declaration of declarations) {
      const idx = declaration.indexOf(":");
      if (idx === -1) {
        kept.push(declaration);
        continue;
      }

      const prop = declaration.slice(0, idx).trim().toLowerCase();
      const value = declaration.slice(idx + 1).trim();

      if (prop === "background-color" && isIgnorableTableBackground(value)) {
        continue;
      }

      kept.push(declaration);
    }

    if (kept.length === 0) {
      cell.removeAttribute("style");
    } else {
      cell.setAttribute("style", kept.join("; "));
    }
  });

  return wrapper.innerHTML;
}

function canonicalizeEditorHtml(html) {
  return sanitizeTableCellBackgrounds(html || "").trim();
}

function seedFromHTML(html) {
  return (editor) => {
    const cleanHtml = canonicalizeEditorHtml(html);
    if (!cleanHtml) return;

    const dom = new DOMParser().parseFromString(cleanHtml, "text/html");

    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const nodes = $generateNodesFromDOM(editor, dom);
      root.append(...nodes);
    });
  };
}

function SetEditablePlugin({ isEditable }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(isEditable);
  }, [editor, isEditable]);

  return null;
}

function SyncFromHTMLPlugin({
  html,
  lastEmittedRef,
  lastAppliedRef,
  syncingRef,
}) {
  const [editor] = useLexicalComposerContext();
  const pendingHtmlRef = useRef(null);
  const hasFocusRef = useRef(false);

  const applyHtml = useCallback(
    (nextHtml) => {
      const cleanHtml = canonicalizeEditorHtml(nextHtml);
      lastAppliedRef.current = cleanHtml;
      syncingRef.current = true;

      const dom = new DOMParser().parseFromString(cleanHtml, "text/html");

      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const nodes = $generateNodesFromDOM(editor, dom);
        root.append(...nodes);
      });

      syncingRef.current = false;
    },
    [editor, lastAppliedRef, syncingRef]
  );

  useLayoutEffect(() => {
    const unregisterFocus = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        hasFocusRef.current = true;
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterBlur = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        hasFocusRef.current = false;

        const pending = pendingHtmlRef.current;
        pendingHtmlRef.current = null;

        if (pending == null) return false;

        const next = canonicalizeEditorHtml(pending);
        if (next === lastEmittedRef.current) return false;
        if (next === lastAppliedRef.current) return false;

        applyHtml(next);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterFocus();
      unregisterBlur();
    };
  }, [editor, applyHtml, lastAppliedRef, lastEmittedRef]);

  useEffect(() => {
    const next = canonicalizeEditorHtml(html || "");

    if (next === lastEmittedRef.current) return;
    if (next === lastAppliedRef.current) return;

    if (hasFocusRef.current) {
      pendingHtmlRef.current = next;
      return;
    }

    applyHtml(next);
  }, [html, applyHtml, lastAppliedRef, lastEmittedRef]);

  return null;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Start typing…",
  readOnly = false,
  minHeight = 160,
  className = "",
  ariaLabel = "Rich text editor",
  autoFocus = false,
}) {
  const initialValue = canonicalizeEditorHtml(value || "");

  const lastEmittedRef = useRef(initialValue);
  const lastAppliedRef = useRef(initialValue);
  const syncingRef = useRef(false);

  const initialConfig = useMemo(
    () => ({
      namespace: "rich-text-editor",
      editable: !readOnly,
      onError(error) {
        console.error(error);
      },
      nodes: NODES,
      theme: {
        paragraph: "m-0",
        text: {
          bold: "fw-bold",
          italic: "fst-italic",
          underline: "text-decoration-underline",
          strikethrough: "text-decoration-line-through",
          underlineStrikethrough:
            "text-decoration-underline text-decoration-line-through",
          code: "bg-light px-1 rounded small font-monospace",
        },
        list: { ol: "ps-4", ul: "ps-4", listitem: "" },
        link: "text-decoration-underline",
        heading: { h1: "h4", h2: "h5", h3: "h6" },
        quote: "border-start ps-3 text-muted fst-italic",
        table: "rte-table",
        tableCell: "rte-table-cell",
        tableCellHeader: "rte-table-cell rte-table-cell-header",
      },
      editorState: seedFromHTML(initialValue),
    }),
    [readOnly, initialValue]
  );

  const handleChange = useCallback(
    (editorState, editor) => {
      if (syncingRef.current) return;

      editorState.read(() => {
        const rawHtml = $generateHtmlFromNodes(editor);
        const cleanHtml = canonicalizeEditorHtml(rawHtml);

        lastEmittedRef.current = cleanHtml;
        onChange?.(cleanHtml);
      });
    },
    [onChange]
  );

  return (
    <div
      className={`rte-wrapper border rounded ${className}`}
      style={{ minHeight }}
    >
      <LexicalComposer initialConfig={initialConfig}>
        {!readOnly && <ToolbarPlugin />}

        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label={ariaLabel}
              className="rte-content p-2"
              style={{ minHeight: Math.max(0, minHeight - 44) }}
            />
          }
          placeholder={<div className="text-muted px-2">{placeholder}</div>}
        />

        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <TablePlugin />
        <HorizontalRulePlugin />
        <ImagePasteDropPlugin />
        <TablePastePlugin />
        {!readOnly && <HoverNodeActionsPlugin isEditable={!readOnly} />}
        {autoFocus && <AutoFocusPlugin />}
        <OnChangePlugin onChange={handleChange} />
        <SetEditablePlugin isEditable={!readOnly} />

        <SyncFromHTMLPlugin
          html={value}
          lastEmittedRef={lastEmittedRef}
          lastAppliedRef={lastAppliedRef}
          syncingRef={syncingRef}
        />
      </LexicalComposer>
    </div>
  );
}